import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import SEO from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";

const loginSchema = z.object({
  identifier: z.string().trim().min(1, { message: "Username or email is required" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

const signupSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
  username: z.string().trim().min(3, { message: "Username must be at least 3 characters" }).max(20, { message: "Username must be less than 20 characters" }).regex(/^[a-zA-Z0-9_]+$/, { message: "Username can only contain letters, numbers, and underscores" }),
});

const Auth = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') === 'login');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, subscription, loading: authLoading } = useAuth();

  // Update form mode when URL changes
  useEffect(() => {
    const currentMode = searchParams.get('mode');
    const shouldBeLogin = currentMode === 'login';
    setIsLogin(shouldBeLogin);
    // Reset form fields when switching modes
    setEmail("");
    setIdentifier("");
    setPassword("");
    setUsername("");
  }, [searchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      if (subscription?.subscribed) {
        window.location.href = 'https://dashboard.oraculusx.com';
      } else {
        navigate('/pricing');
      }
    }
  }, [user, subscription, authLoading, navigate]);

  const checkAndRedirect = async (session: any) => {
    // Check subscription status
    const { data, error } = await supabase.functions.invoke('check-subscription', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (!error && data?.subscribed) {
      // Has active subscription -> Dashboard
      window.location.href = 'https://dashboard.oraculusx.com';
    } else {
      // No subscription -> Pricing
      navigate('/pricing');
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Validate login input
        const validated = loginSchema.parse({
          identifier,
          password,
        });

        let email = validated.identifier;

        // If identifier is not an email, look up the email from username
        if (!validated.identifier!.includes('@')) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('email')
            .eq('username', validated.identifier)
            .single();

          if (profileError || !profileData) {
            throw new Error("Invalid username or password");
          }

          email = profileData.email;
        }

        // Use standard Supabase client-side authentication
        const { data: { session }, error: signInError } = await supabase.auth.signInWithPassword({
          email: email!,
          password: validated.password,
        });

        if (signInError) {
          throw new Error("Invalid email/username or password");
        }

        if (!session) {
          throw new Error("Login failed - no session created");
        }

        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });

        await checkAndRedirect(session);
      } else {
        // Validate signup input
        const validated = signupSchema.parse({
          email,
          password,
          username,
        });

        const redirectUrl = `${window.location.origin}/`;
        
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: validated.email!,
          password: validated.password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              username: validated.username,
            },
          },
        });

        if (signUpError) {
          if (signUpError.message.includes("already registered")) {
            throw new Error("This email is already registered. Please sign in instead.");
          }
          throw signUpError;
        }

        // Sign in immediately after signup (since auto-confirm is enabled)
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: validated.email!,
          password: validated.password,
        });

        if (signInError) {
          toast({
            title: "Account created!",
            description: "Please sign in to continue.",
          });
          setIsLogin(true);
          return;
        }

        // Get session and redirect based on subscription
        const session = signInData.session;
        if (session) {
          toast({
            title: "Account created!",
            description: "Welcome to Oraculus:X!",
          });

          // Always redirect to pricing for new signups
          navigate('/pricing');
        }
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else if (error instanceof Error) {
        toast({
          title: "Authentication Error",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <SEO 
        title={isLogin ? "Sign In | Oraculus:X" : "Sign Up | Oraculus:X"}
        description={isLogin ? "Sign in to access your premium crypto trading signals" : "Create your account to start your free trial"}
      />
      <div className="fixed inset-0 bg-gradient-mesh animate-mesh-move opacity-40" />
      <div className="fixed inset-0 bg-gradient-opal-radial animate-gradient-shift" style={{ backgroundSize: '200% 200%' }} />
      
      <div className="container px-4 relative z-10">
        <Card className="max-w-md mx-auto p-8 bg-gradient-primary relative overflow-hidden">
          <div className="absolute inset-0 bg-card/80 backdrop-blur-2xl" />
          <div className="relative z-10">
            <h1 className="text-3xl font-bold text-center mb-2">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-center text-muted-foreground mb-8">
              {isLogin ? "Sign in to access your dashboard" : "Start your 7-day free trial"}
            </p>

            <form onSubmit={handleAuth} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="johndoe"
                      required={!isLogin}
                    />
                    <p className="text-xs text-muted-foreground">
                      3-20 characters, letters, numbers, and underscores only
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required={!isLogin}
                    />
                  </div>
                </>
              )}

              {isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="identifier">Username or Email</Label>
                  <Input
                    id="identifier"
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="username or email"
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-primary hover:shadow-glow-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isLogin ? "Signing in..." : "Creating account..."}
                  </>
                ) : (
                  <>{isLogin ? "Sign In" : "Sign Up"}</>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate(`/auth?mode=${isLogin ? 'signup' : 'login'}`)}
                className="text-sm text-primary hover:underline"
              >
                {isLogin
                  ? "Don't have an account? Sign up"
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
